import type { TEref, TFurnitureCountertopSeries, TFurnitureDeliveryDate } from "types/elevationReferences.type"

/**
 * Resolves what a furniture's delivery_date should optimistically become after it connects to
 * `nextEref` (or disconnects entirely, when `nextEref` is undefined).
 *
 * Mirrors the backend's own priority: an explicit override (is_inherited: false) survives eref
 * changes untouched — the backend resolves it before ever looking at the eref. Only a furniture
 * that was inheriting needs its date recomputed: the new eref's own date (still inherited), or
 * null when there is no eref to inherit from (disconnecting, or the new eref has no date).
 */
export const resolveDeliveryDateAfterErefChange = (
	currentDeliveryDate: TFurnitureDeliveryDate | null,
	nextEref: TEref | undefined,
): TFurnitureDeliveryDate | null => {
	const hadOwnOverride = currentDeliveryDate != null && currentDeliveryDate.is_inherited === false
	if (hadOwnOverride) return currentDeliveryDate

	const erefDate = nextEref?.estimation_config?.delivery_date
	if (!erefDate) return null

	return {
		id: erefDate.id,
		date: erefDate.date,
		project_name: erefDate.project_name ?? null,
		is_inherited: true,
	}
}

/**
 * Resolves what a furniture's countertop_series_and_colors (REQ-405) should optimistically become
 * after it connects to `nextEref` (or disconnects entirely, when `nextEref` is undefined).
 *
 * Same priority as {@link resolveDeliveryDateAfterErefChange}: an own override survives untouched.
 * Unlike the delivery date, the eref only carries the series' id (`estimation_config` has no nested
 * name for it, the way it does for `delivery_date`) — so a furniture that starts inheriting a new
 * eref's series resolves with the right id and `is_inherited: true` but an empty `name`. The Select
 * that renders this value looks its label up from the loaded options by id, not from this `name`, so
 * the empty placeholder is never shown; it is corrected for free once that eref's own options load.
 */
export const resolveCountertopSeriesAfterErefChange = (
	currentSeries: TFurnitureCountertopSeries | null,
	nextEref: TEref | undefined,
): TFurnitureCountertopSeries | null => {
	const hadOwnOverride = currentSeries != null && currentSeries.is_inherited === false
	if (hadOwnOverride) return currentSeries

	const erefSeriesId = nextEref?.estimation_config?.countertop_series_and_colors_id
	if (erefSeriesId == null) return null

	return { id: erefSeriesId, name: "", is_inherited: true }
}
